import { Application, Container } from '@tencent/alloy-renderer';
import { BackgroundService } from './background-service';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IRenderProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/stable-layer/stable-layer.interface';

/**
 * 背景图manager
 * 维护、切换背景图
 */
export class BackgroundManager {
    private scale: number;
    private backGroundCacheMap: Map<ISlideModel, BackgroundService>;
    private currentSlideModel?: ISlideModel;
    private handleStableLayerUpdate: (props: Partial<IRenderProps>) => void;
    private getApplication: () => Application;

    public constructor(
        scale = 1,
        handleStableLayerUpdate: (props: Partial<IRenderProps>) => void,
        getApplication: () => Application,
    ) {
        this.scale = scale;
        this.handleStableLayerUpdate = handleStableLayerUpdate;
        this.backGroundCacheMap = new Map();
        this.getApplication = getApplication;
    }

    public renderBackgroundByCache = (slideModel: ISlideModel): void => {
        const backgroundCache = this.getBackgroundFromCache(slideModel);
        if (!backgroundCache) {
            this.buildBackgroundService(slideModel);
        } else {
            backgroundCache.renderBackgroundComponent();
        }
    };

    /**
     * 删除这个背景的dom节点与监听
     * @param slideModel
     */
    public cleanBackground = (slideModel?: ISlideModel): void => {
        if (!slideModel) return;
        const backgroundService = this.backGroundCacheMap.get(slideModel);
        if (backgroundService) {
            // 注销这个背景的监听
            backgroundService.removeListener();
        }
        this.backGroundCacheMap.delete(slideModel);
    };

    /**
     * 查找缓存中是否有该背景
     * @param slideModel
     */
    public getBackgroundFromCache = (slideModel: ISlideModel): BackgroundService | undefined => this
        .backGroundCacheMap.get(slideModel);

    /**
     * 构建一个新的背景并缓存
     * @param slideModel
     */
    public buildBackgroundService = (slideModel: ISlideModel) => {
        // 创建背景
        const backGroundService = new BackgroundService(
            this.scale,
            slideModel,
            this.handleStableLayerUpdate,
            this.getApplication,
        );

        // 缓存背景
        this.cacheBackground(slideModel, backGroundService);

        // 如果是为当前页构建，则展示背景
        if (this.getAppServices().viewableServiceLayer.selectionService.currentSlide === slideModel) {
            this.updateBackground();
        }

        backGroundService.renderBackgroundComponent();
    };

    /**
     * 根据slideModel展示背景，并按需更新组件
     */
    public updateBackground = (): void => {
        const slideModel = this.getAppServices().viewableServiceLayer.selectionService.currentSlide;
        // 如果还是当前背景，则不需要刷新
        if (slideModel === this.currentSlideModel) return;

        // 展示新背景
        if (slideModel) {
            this.currentSlideModel = slideModel;
            const backGroundService = this.backGroundCacheMap.get(slideModel);
            if (backGroundService) {
                // 按需刷新背景，由内层判断需不需要重刷
                backGroundService.updateWithData(this.scale);
            } else {
                // 防止找不到背景，重新构建一个
                this.renderBackgroundByCache(slideModel);
                this.updateBackground();
            }
        }
    };

    /**
     * 更新manager的宽高数据
     * 更新时，如果宽高变化了，只重刷当前背景，其他背景不刷
     * 切页其他背景时，由背景对比自身与manager的宽高是否不同来决定是否重刷
     * */
    public updateWithData = (scale: number): void => {
        this.scale = scale;
        this.updateServiceInfo(this.currentSlideModel);
    };

    /**
     * 获取背景渲染树
     * @param slideModel
     */
    public getRenderComponent = (slideModel: ISlideModel): Container | undefined => {
        const backgroundService = this.getBackgroundFromCache(slideModel);
        return backgroundService?.getComponent();
    };

    /**
     * 更新指定service数据
     * @param slideModel
     */
    public updateServiceInfo = (slideModel?: ISlideModel): void => {
        if (slideModel) {
            this.backGroundCacheMap.get(slideModel)?.updateWithData?.(this.scale);
        }
    };

    /**
     * 缓存背景canvas元素
     */
    private cacheBackground(slideModel: ISlideModel, backgroundService: BackgroundService): void {
        this.backGroundCacheMap.set(slideModel, backgroundService);
    }

    private getAppServices(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IPcEditorAppServices;
    }
}

